<template>
    <section class="px-3 lg:px-28 py-16 lg:py-24">
        <div class="text-center mb-8">
            <h2 class="font-semibold">Check your integration backlog</h2>
            <p class="subtitle mt-4 max-w-3xl mx-auto">
                List the tools your sales team keeps hearing about, separated by commas, and see which are already
                live.
            </p>
        </div>

        <div class="max-w-3xl mx-auto">
            <label for="coverage-input" class="sr-only">Integrations to check</label>
            <input id="coverage-input" v-model="query" type="text" autocomplete="off"
                placeholder="e.g. Salesforce, Workday, Greenhouse, NetSuite"
                class="w-full rounded-lg border bg-[var(--dark-gray)] px-4 py-3 text-base text-[var(--light-gray)] placeholder:text-[var(--medium-gray)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--secondary-blue)]" />

            <ul v-if="results.length" class="mt-6 divide-y divide-[var(--border-gray)] rounded-lg border" aria-live="polite">
                <li v-for="result in results" :key="result.term" class="flex flex-wrap items-center gap-3 px-4 py-3">
                    <template v-if="result.match">
                        <Icon name="mdi:check-circle" class="text-lg text-[var(--primary-green)]" aria-hidden="true" />
                        <a :href="`/integrations/${result.match.type}`"
                            class="font-semibold text-[var(--light-gray)] hover:text-[var(--primary-blue)]">
                            {{ result.match.name }}
                        </a>
                        <span class="text-medium-gray text-sm">Live</span>
                        <span class="flex flex-wrap gap-1 ml-auto">
                            <Badge v-for="cat in result.match.categories.slice(0, 3)" :key="cat"
                                :label="LABELS[cat] || cat.toUpperCase()" />
                        </span>
                    </template>
                    <template v-else>
                        <Icon name="mdi:help-circle-outline" class="text-lg text-[var(--medium-gray)]" aria-hidden="true" />
                        <span class="font-semibold text-[var(--light-gray)]">{{ result.term }}</span>
                        <span class="text-medium-gray text-sm">Not found by that name</span>
                        <a href="/integrations/roadmap"
                            class="ml-auto text-sm text-[var(--secondary-blue)] hover:text-[var(--primary-blue)] underline">
                            Check the roadmap or request it
                        </a>
                    </template>
                </li>
            </ul>

            <p v-if="results.length" class="mt-4 text-center text-sm text-medium-gray">
                {{ liveCount }} of {{ results.length }} live today.
                <a href="/integrations" class="text-[var(--light-gray)] hover:text-[var(--primary-blue)] underline">
                    Browse all {{ INTEGRATIONS_COUNT_10s }}+ integrations
                </a>
            </p>
        </div>
    </section>
</template>

<script lang="ts">
import { INTEGRATIONS, INTEGRATIONS_COUNT_10s, type IIntegration2 } from '~~/services/integrations';
import { MAP_CATS } from '~~/services/categories';

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export default {
    data() {
        return {
            query: '',
            INTEGRATIONS_COUNT_10s,
            LABELS: MAP_CATS as Record<string, string>,
        };
    },
    computed: {
        results(): { term: string; match?: IIntegration2 }[] {
            const terms = [...new Set(this.query.split(/[,\n]/).map((t) => t.trim()).filter((t) => normalize(t).length > 1))];

            return terms.slice(0, 20).map((term) => {
                const key = normalize(term);
                const integrations = INTEGRATIONS as IIntegration2[];
                // Prefer an exact name/type match, then a name that starts with the term.
                const match =
                    integrations.find((i) => normalize(i.name) === key || normalize(i.type) === key) ||
                    integrations.find((i) => normalize(i.name).startsWith(key));
                return { term, match };
            });
        },
        liveCount(): number {
            return this.results.filter((r) => r.match).length;
        },
    },
};
</script>
